import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Typography,
  Box,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { ExpandLess, ExpandMore, Add } from '@mui/icons-material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

import EndpointChipsSummary from './EndpointChipsSummary';
import EndpointOverridesTable from './EndpointOverridesTable';
import GroupEndpointsTable from './GroupEndpointsTable';
import AvailableGroupsDrawer from './AvailableGroupsDrawer';

const StyledPaper = styled(props => {
  return <Paper elevation={1} {...props} />;
})(({ theme }) => {
  return {
    width: '100%',
    background:
      theme.palette.type === 'light' ? 'white' : theme.palette.paper.main,
  };
});

export default function EndpointAccessManager({
  client,
  clientPermissions,
  loadingClientPermissions,
  allGroups,
  isLoadingAllGroups,
  getAllGroups,
  getPermissions,
  onPermissionsChange,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dataSynced, setDataSynced] = useState(false);
  const [overrides, setOverrides] = useState([]); // Array of: { path, method, type: 'grant' | 'revoke' }
  const [assignedGroups, setAssignedGroups] = useState([]);

  const allClientGroups = useMemo(
    () => clientPermissions?.groups || [],
    [clientPermissions],
  );
  const catalogGroups = useMemo(() => {
    return allGroups?.groups || (Array.isArray(allGroups) ? allGroups : []);
  }, [allGroups]);

  useEffect(() => {
    getAllGroups();
  }, [getAllGroups]);

  useEffect(() => {
    if (client?.client_id) {
      getPermissions(client.client_id);
    }
  }, [client, getPermissions]);

  useEffect(() => {
    if (clientPermissions && !loadingClientPermissions && !isLoadingAllGroups) {
      // Compatibility with the actual JSON from the backend
      if (clientPermissions.assigned_groups) {
        setAssignedGroups(clientPermissions.assigned_groups);

        const enrichedOverrides = (clientPermissions.overrides || []).map(o => {
          let catalogEndpoint = null;
          for (const group of catalogGroups) {
            const found = group.endpoints.find(
              ep => ep.path === o.path && ep.method === o.method,
            );
            if (found) {
              catalogEndpoint = found;
              break;
            }
          }

          return {
            ...catalogEndpoint, // Inject extra info (parameters, summary) from the catalog
            ...o, // Overwrites the base info returned by the backend (path, method)
            type: o.override_type, // Maps override_type to type for the UI
          };
        });

        setOverrides(enrichedOverrides);
        setDataSynced(true);
      } else {
        setAssignedGroups([]);
        setOverrides([]);
        setDataSynced(true);
      }
    }
  }, [
    clientPermissions,
    loadingClientPermissions,
    isLoadingAllGroups,
    catalogGroups,
  ]);

  useEffect(() => {
    if (dataSynced && onPermissionsChange) {
      onPermissionsChange({ assignedGroups, overrides });
    }
  }, [assignedGroups, overrides, dataSynced, onPermissionsChange]);

  const handleToggleGroup = groupName => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const handleToggleOverride = (endpoint, type) => {
    setOverrides(prev => {
      const exists = prev.find(
        o => o.path === endpoint.path && o.method === endpoint.method,
      );
      if (exists && exists.type === type) {
        return prev.filter(
          o => !(o.path === endpoint.path && o.method === endpoint.method),
        );
      } else if (exists) {
        return prev.map(o =>
          o.path === endpoint.path && o.method === endpoint.method
            ? { ...o, type }
            : o,
        );
      } else {
        return [...prev, { ...endpoint, type }];
      }
    });
  };

  const getOverride = endpoint =>
    overrides.find(
      o => o.path === endpoint.path && o.method === endpoint.method,
    );

  const handleAddGroup = groupName => {
    if (!assignedGroups.includes(groupName)) {
      setAssignedGroups(prev => [...prev, groupName]);
      const group =
        catalogGroups.find(g => g.group_name === groupName) ||
        allClientGroups.find(g => g.group_name === groupName);
      if (group) {
        setOverrides(prev =>
          prev.filter(
            o =>
              !(
                o.type === 'grant' &&
                group.endpoints.some(
                  ep => ep.path === o.path && ep.method === o.method,
                )
              ),
          ),
        );
      }
    }
  };

  const handleRemoveGroup = groupName => {
    setAssignedGroups(prev => prev.filter(g => g !== groupName));
    const group =
      catalogGroups.find(g => g.group_name === groupName) ||
      allClientGroups.find(g => g.group_name === groupName);
    if (group) {
      setOverrides(prev =>
        prev.filter(
          o =>
            !(
              o.type === 'revoke' &&
              group.endpoints.some(
                ep => ep.path === o.path && ep.method === o.method,
              )
            ),
        ),
      );
    }
  };

  return (
    <StyledPaper sx={{ p: 3, height: '100%', mb: 2, borderRadius: 2 }}>
      <Box sx={{ minHeight: 200, maxHeight: 600, overflowY: 'auto', pr: 1 }}>
        {loadingClientPermissions ? (
          <LinearProgress
            color="secondary"
            sx={{ my: 4, borderRadius: '10rem' }}
          />
        ) : (
          <>
            {/* Header: Title + Modular Endpoint Chip Summary Dashboard + ADD GROUP Button */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                {t('cws_app.management.assigned_groups', 'Assigned Groups')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <EndpointChipsSummary
                  assignedGroups={assignedGroups}
                  catalogGroups={catalogGroups}
                  allClientGroups={allClientGroups}
                  overrides={overrides}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    backgroundColor: theme.palette.text.primary,
                    color: theme.palette.background.paper,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: theme.palette.text.secondary,
                    },
                  }}>
                  {t('cws_app.management.add_group', 'ADD GROUP')}
                </Button>
              </Box>
            </Box>

            <List disablePadding>
              {assignedGroups.map(groupName => {
                const group =
                  catalogGroups.find(g => g.group_name === groupName) ||
                  allClientGroups.find(g => g.group_name === groupName);
                if (!group) return null;
                const isExpanded = !!expandedGroups[groupName];

                const totalEndpoints = group.endpoints?.length || 0;
                const revokedCount = overrides.filter(
                  o =>
                    o.type === 'revoke' &&
                    group.endpoints?.some(
                      ep => ep.path === o.path && ep.method === o.method,
                    ),
                ).length;
                const activeEndpoints = totalEndpoints - revokedCount;

                return (
                  <React.Fragment key={groupName}>
                    <ListItem
                      sx={{
                        bgcolor: 'action.hover',
                        mb: 0.5,
                        borderRadius: 1,
                        py: 0.75,
                        px: 2,
                      }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleGroup(groupName)}
                        sx={{ mr: 1 }}>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <ListItemText
                        primary={groupName}
                        secondary={`${activeEndpoints} / ${totalEndpoints} ${t(
                          'cws_app.management.endpoints',
                          'endpoints',
                        )}`}
                        primaryTypographyProps={{
                          fontWeight: 'bold',
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          display: 'inline-block',
                          ml: 1.5,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveGroup(groupName)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'secondary.main',
                          },
                        }}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </ListItem>
                    <Collapse
                      in={isExpanded}
                      timeout="auto"
                      unmountOnExit
                      sx={{ width: '100%', mb: 1.5 }}>
                      {/* High-quality styled Table for group endpoints matching designs */}
                      <GroupEndpointsTable
                        group={group}
                        overrides={overrides}
                        getOverride={getOverride}
                        onToggleOverride={handleToggleOverride}
                      />
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>

            {/* Modular Custom Overrides Table Component */}
            <EndpointOverridesTable
              overrides={overrides}
              onRemoveOverride={handleToggleOverride}
            />
          </>
        )}
      </Box>

      {/* Modular Available Groups Side Drawer Component */}
      <AvailableGroupsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        catalogGroups={catalogGroups}
        assignedGroups={assignedGroups}
        allClientGroups={allClientGroups}
        overrides={overrides}
        handleAddGroup={handleAddGroup}
        handleRemoveGroup={handleRemoveGroup}
        handleToggleOverride={handleToggleOverride}
        isLoadingAllGroups={isLoadingAllGroups}
      />
    </StyledPaper>
  );
}
